import { BriefcaseBusiness } from 'lucide-react';
import { useState } from 'react';
import Card from '../../../components/commoncomponents/Card';
import EmptyState from '../../../components/commoncomponents/EmptyState';
import BusinessBrandLogoModal from './BusinessBrandLogoModal';
import BusinessFlagColorModal from './BusinessFlagColorModal';
import BusinessPortalCard from './BusinessPortalCard';
import BusinessRenameModal from './BusinessRenameModal';
import { DEFAULT_FLAG_COLOR } from './businessCardFlagColors';
import { isBusinessOpen, isLaunchableBusiness } from '../../../utils/portalRegistry';

function BusinessPortalView({
  businesses,
  openingBusinessId = null,
  selectedBusinessId,
  onOpenBusiness,
  onOpenUserManagement,
  onSelectBusiness,
  onReorderBusinesses,
  businessFlagColors = {},
  onChangeBusinessLogo,
  onChangeBusinessFlagColor,
  onRenameBusiness,
}) {
  const [draggedBusinessId, setDraggedBusinessId] = useState(null);
  const [renameBusiness, setRenameBusiness] = useState(null);
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [brandLogoBusiness, setBrandLogoBusiness] = useState(null);
  const [flagColorBusiness, setFlagColorBusiness] = useState(null);
  const [flagColorError, setFlagColorError] = useState('');
  const [brandLogoSaving, setBrandLogoSaving] = useState(false);
  const [brandLogoError, setBrandLogoError] = useState('');

  if (!businesses.length) {
    return (
      <Card className="p-6 sm:p-8">
        <EmptyState
          icon={BriefcaseBusiness}
          title="No assigned businesses yet"
          message="Once a business is assigned to this admin account, it will appear here for selection."
          showAction={false}
        />
      </Card>
    );
  }

  function moveBusiness(fromBusinessId, toBusinessId) {
    if (!fromBusinessId || !toBusinessId || fromBusinessId === toBusinessId) {
      return;
    }

    const fromIndex = businesses.findIndex((business) => business.id === fromBusinessId);
    const toIndex = businesses.findIndex((business) => business.id === toBusinessId);

    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return;
    }

    moveBusinessByIndex(fromIndex, toIndex);
  }

  function moveBusinessByIndex(fromIndex, toIndex) {
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= businesses.length ||
      toIndex >= businesses.length ||
      fromIndex === toIndex
    ) {
      return;
    }

    const nextBusinesses = [...businesses];
    const [movedBusiness] = nextBusinesses.splice(fromIndex, 1);
    nextBusinesses.splice(toIndex, 0, movedBusiness);
    onReorderBusinesses(nextBusinesses);
  }

  async function handleSaveBusinessLogo(logoUrl) {
    if (!brandLogoBusiness || !onChangeBusinessLogo) {
      return;
    }

    setBrandLogoSaving(true);
    setBrandLogoError('');

    try {
      if (!logoUrl?.file && !logoUrl?.removeLogo) {
        setBrandLogoBusiness(null);
        return;
      }

      await onChangeBusinessLogo(brandLogoBusiness, logoUrl);
      setBrandLogoBusiness(null);
    } catch (requestError) {
      setBrandLogoError(requestError.message || 'Failed to update the business logo.');
    } finally {
      setBrandLogoSaving(false);
    }
  }

  async function handleSaveBusinessName(businessName) {
    if (!renameBusiness || !onRenameBusiness) {
      return;
    }

    if (!businessName) {
      setRenameError('Business name is required.');
      return;
    }

    setRenameSaving(true);
    setRenameError('');

    try {
      await onRenameBusiness(renameBusiness, businessName);
      setRenameBusiness(null);
    } catch (requestError) {
      setRenameError(requestError.message || 'Failed to rename this business.');
    } finally {
      setRenameSaving(false);
    }
  }

  async function handleSelectFlagColor(colorValue) {
    if (!flagColorBusiness || !onChangeBusinessFlagColor) {
      return;
    }

    setFlagColorError('');

    try {
      await onChangeBusinessFlagColor(flagColorBusiness, colorValue);
      setFlagColorBusiness(null);
    } catch (requestError) {
      setFlagColorError(requestError.message || 'Failed to update the business portal theme.');
    }
  }

  return (
    <>
      <div className="grid gap-4 sm:gap-5">
      <div>
        <h2 className="m-0 text-xl font-semibold tracking-[-0.03em] text-[#111111] sm:text-3xl">
          Our Businesses
        </h2>
        <p className="mt-1.5 text-sm text-[#8b7a6d] sm:mt-2 sm:text-base">
          Choose a business portal to continue.
        </p>
        <p className="mt-2 text-[13px] text-[#8b7a6d] sm:text-sm">
          Drag a card to rearrange your portal order. Your layout will be saved for your next login.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {businesses.map((business, index) => (
          <BusinessPortalCard
            key={business.id}
            business={business}
            canMoveDown={index < businesses.length - 1}
            canMoveUp={index > 0}
            isSelected={business.id === selectedBusinessId}
            isBusinessOpen={isBusinessOpen}
            isOpening={openingBusinessId === business.id}
            isLaunchable={isLaunchableBusiness(business)}
            onMoveDown={() => moveBusinessByIndex(index, index + 1)}
            onMoveUp={() => moveBusinessByIndex(index, index - 1)}
            onOpenBusiness={onOpenBusiness}
            onOpenUserManagement={onOpenUserManagement}
            onSelectBusiness={onSelectBusiness}
            onChangeBrandLogo={(selectedBusiness) => {
              setBrandLogoError('');
              setBrandLogoBusiness(selectedBusiness);
            }}
            onChangeFlagColor={(selectedBusiness) => {
              setFlagColorError('');
              setFlagColorBusiness(selectedBusiness);
            }}
            onRenameBusiness={(selectedBusiness) => {
              setRenameError('');
              setRenameBusiness(selectedBusiness);
            }}
            flagColor={businessFlagColors[business.id] || DEFAULT_FLAG_COLOR}
            isDragging={draggedBusinessId === business.id}
            onDragStart={() => setDraggedBusinessId(business.id)}
            onDragEnd={() => setDraggedBusinessId(null)}
            onDragEnter={() => moveBusiness(draggedBusinessId, business.id)}
          />
        ))}
      </div>
      </div>

      <BusinessRenameModal
        business={renameBusiness}
        isOpen={Boolean(renameBusiness)}
        saving={renameSaving}
        error={renameError}
        onClose={() => {
          if (!renameSaving) {
            setRenameBusiness(null);
            setRenameError('');
          }
        }}
        onSave={handleSaveBusinessName}
      />

      <BusinessBrandLogoModal
        business={brandLogoBusiness}
        isOpen={Boolean(brandLogoBusiness)}
        saving={brandLogoSaving}
        error={brandLogoError}
        onClose={() => {
          if (!brandLogoSaving) {
            setBrandLogoBusiness(null);
            setBrandLogoError('');
          }
        }}
        onSave={handleSaveBusinessLogo}
      />

      <BusinessFlagColorModal
        business={flagColorBusiness}
        isOpen={Boolean(flagColorBusiness)}
        error={flagColorError}
        selectedColor={
          flagColorBusiness
            ? businessFlagColors[flagColorBusiness.id] || DEFAULT_FLAG_COLOR
            : DEFAULT_FLAG_COLOR
        }
        onClose={() => {
          setFlagColorBusiness(null);
          setFlagColorError('');
        }}
        onSelectColor={handleSelectFlagColor}
      />
    </>
  );
}

export default BusinessPortalView;
